import { Link } from "react-router-dom";
import { CaretRight, User, Wrench, CheckCircle, Receipt } from "@phosphor-icons/react";
import { fmtDate } from "../../lib/dateUtil";
import { SUB_JOB_STATUS } from "./SubJobProgress";
import { fmtMoney } from "../quotes/PortalQuoteActions";

/* Approved / awaiting variation-quote value for one sub-job (ex VAT) */
export function SubJobQuoteChip({ quotes, testid }) {
  if (!quotes || (!quotes.approved_count && !quotes.pending_count)) return null;
  return (
    <span className="inline-flex items-center gap-1 shrink-0" data-testid={testid}>
      {quotes.approved_count > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200 tabular-nums"
          title={`${quotes.approved_count} approved quote${quotes.approved_count === 1 ? "" : "s"} · ${fmtMoney(quotes.approved_total)} ex VAT`} data-testid={testid ? `${testid}-approved` : undefined}>
          <Receipt size={10} weight="fill" /> {fmtMoney(quotes.approved_total)}
        </span>
      )}
      {quotes.pending_count > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 tabular-nums"
          title={`${quotes.pending_count} quote${quotes.pending_count === 1 ? "" : "s"} awaiting client approval · ${fmtMoney(quotes.pending_total)} ex VAT`} data-testid={testid ? `${testid}-pending` : undefined}>
          {fmtMoney(quotes.pending_total)} awaiting
        </span>
      )}
    </span>
  );
}

/* One sub-job line: index · reference · title · assignee · scheduled · quote value · status */
export default function SubJobRow({ job, to, actions, compact = false, testid, className = "" }) {
  const st = SUB_JOB_STATUS[job.status] || SUB_JOB_STATUS.scheduled;
  const date = job.schedule?.start_date ? fmtDate(job.schedule.start_date) : "";
  const inner = (
    <>
      <span className="h-6 w-6 shrink-0 rounded-full bg-muted text-[10px] font-bold flex items-center justify-center text-muted-foreground" data-testid={testid ? `${testid}-index` : undefined}>{job.sub_job_index || "•"}</span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold truncate flex items-center gap-1.5">
          <span className="font-mono text-primary text-[11px]">{job.reference}</span>
          <span className="truncate">{job.title}</span>
          {job.signed_off && <CheckCircle size={12} weight="fill" className="text-emerald-600 shrink-0" title="Client signed off" />}
        </p>
        {!compact && (
          <p className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
            {job.assigned_operative_name ? <span className="inline-flex items-center gap-1"><User size={10} /> {job.assigned_operative_name}</span>
              : job.assigned_vendor_name ? <span className="inline-flex items-center gap-1"><Wrench size={10} /> {job.assigned_vendor_name}</span>
              : <span className="italic">Unassigned</span>}
            <span>{date ? `${date}${job.schedule?.start_time ? ` ${job.schedule.start_time}` : ""}` : "Not scheduled"}</span>
            {job.category && job.category !== "general" && <span className="capitalize">{job.category.replace(/_/g, " ")}</span>}
          </p>
        )}
      </div>
      {!compact && <SubJobQuoteChip quotes={job.quotes} testid={testid ? `${testid}-quotes` : undefined} />}
      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${st.color}`} data-testid={testid ? `${testid}-status` : undefined}>{st.label}</span>
    </>
  );
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid={testid}>
      {to ? (
        <Link to={to} className="flex items-center gap-2.5 flex-1 min-w-0 rounded-md px-2 py-2 hover:bg-muted/50 transition-colors" data-testid={testid ? `${testid}-link` : undefined}>
          {inner}
          <CaretRight size={12} className="text-muted-foreground shrink-0" />
        </Link>
      ) : <div className="flex items-center gap-2.5 flex-1 min-w-0 px-2 py-2">{inner}</div>}
      {actions}
    </div>
  );
}
